import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { FormFieldStyle, FormInputWrapper } from "@insite/mobius/FormField";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { InputStyle } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasProductContext & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});
export interface ProductDetailSubCustomContainerStyles {
    container?: GridContainerProps;
    leftColumnGridItemStyle?: GridItemProps;
    rightColumnGridItemStyle?: GridItemProps;
    rightColumnInnerContainer?: GridContainerProps;
    topGridItem?: GridItemProps;
    bottomGridItem?: GridItemProps;
    productDetailsActionStyles?: InjectableCss;
}

export const productDetailSubCustomContainerStyles: ProductDetailSubCustomContainerStyles = {
    productDetailsActionStyles: {
        css: css`
            display: flex;
            flex-direction: column;
            > ${TypographyStyle} {
                > ${TypographyStyle} {
                    font-weight: 600;
                }
            }
        `,
    },
    container: {
        gap: 20,
    },
    leftColumnGridItemStyle: {
        width: [12, 12, 8, 8, 8],
    },
    rightColumnGridItemStyle: {
        width: [12, 12, 4, 4, 4],
    },
    topGridItem: {
        width: 12,
        css: css`
            > div[class^="Wrapper"] {
                > section[class^="sc"] {
                    margin-bottom: 5px;
                }
                > ${FormFieldStyle} {
                    flex-direction: row;
                    margin-bottom: 10px;
                    width: 65%;
                    min-width: 65%;
                    > ${FormInputWrapper} {
                        margin-top: 0px !important;
                        > ${InputStyle} {
                            :focus {
                                border: 2px solid ${getColor("primary")} !important;
                            }
                        }
                    }
                }
            }
        `,
    },
    bottomGridItem: {
        width: 12,
    },
};

const styles = productDetailSubCustomContainerStyles;

const ProductDetailSubCustomContainer: FC<Props> = ({ productContext, id }) => {
    const productListingPrice = productContext.productInfo.pricing?.unitRegularPrice;
    const productQuoteRequired = productContext.product.quoteRequired;
    const productActionsSiteMessage = siteMessage("ProductActions_SiteMessage");
    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.leftColumnGridItemStyle}>
                <Zone contentId={id} zoneName="Content00" />
            </GridItem>
            <GridItem {...styles.rightColumnGridItemStyle}>
                <GridContainer {...styles.rightColumnInnerContainer}>
                    <GridItem {...styles.topGridItem}>
                        {productListingPrice! < 1 && productQuoteRequired === false ? (
                            <StyledWrapper {...styles.productDetailsActionStyles}>
                                <Typography>
                                    {translate("Price:")}&nbsp; <Typography>Requires Quote</Typography>
                                </Typography>
                                <Typography>{productActionsSiteMessage}</Typography>
                            </StyledWrapper>
                        ) : (
                            <Zone contentId={id} zoneName="Content01" />
                        )}
                    </GridItem>
                    <GridItem {...styles.bottomGridItem}>
                        <Zone contentId={id} zoneName="Content02" />
                    </GridItem>
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProductContext(ProductDetailSubCustomContainer)),
    definition: {
        group: "Common",
        displayName: "Product Detail Container",
    },
};

export default widgetModule;
